import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createDealsBanner, deleteDealsBanner, getHubs, getOmsDealsBanners } from '../config/api';
import './DealsBanners.css';

const EMPTY_FORM = {
  actionType: 'route',
  routePath: '',
  htmlContent: '',
  position: '0',
  warehouseIds: [],
  showAllWarehouses: false,
  version: '',
  image: null,
};

export default function DealsBanners() {
  const [banners, setBanners] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [hubPickerOpen, setHubPickerOpen] = useState(false);
  const [showComposer, setShowComposer] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [fileInputKey, setFileInputKey] = useState(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bannersResponse, hubsResponse] = await Promise.all([getOmsDealsBanners(), getHubs()]);
      setBanners(bannersResponse?.data || []);
      setHubs(hubsResponse?.data || []);
    } catch {
      toast.error('Failed to load deals banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const handleImageChange = (file) => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setField('image', file || null);
    setImagePreview(file ? URL.createObjectURL(file) : '');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.image) {
      toast.error('Please select a banner image');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(form.image.type)) {
      toast.error('Banner image must be a JPEG, PNG, or WebP file');
      return;
    }
    if (!form.showAllWarehouses && form.warehouseIds.length === 0) {
      toast.error('Please select at least one hub');
      return;
    }
    if (form.actionType === 'route' && !form.routePath.trim()) {
      toast.error('Please enter an app route');
      return;
    }
    if (form.actionType === 'html' && !form.htmlContent.trim()) {
      toast.error('Please enter HTML content');
      return;
    }

    setSaving(true);
    try {
      await createDealsBanner(form);
      toast.success('Banner added successfully');
      setForm(EMPTY_FORM);
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImagePreview('');
      setFileInputKey((current) => current + 1);
      setHubPickerOpen(false);
      setShowComposer(false);
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to add banner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Delete this banner? This cannot be undone.')) return;

    setDeletingId(bannerId);
    try {
      await deleteDealsBanner(bannerId);
      setBanners((current) => current.filter((banner) => banner.id !== bannerId));
      toast.success('Banner deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete banner');
    } finally {
      setDeletingId(null);
    }
  };

  const hubName = (warehouseId) => {
    if (warehouseId === null || warehouseId === undefined) return 'All warehouses';
    return hubs.find((hub) => Number(hub.id) === Number(warehouseId))?.name || 'Unknown hub';
  };
  const setField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const toggleHub = (hubId) => setForm((current) => ({
    ...current,
    warehouseIds: current.warehouseIds.includes(String(hubId))
      ? current.warehouseIds.filter((selectedHubId) => selectedHubId !== String(hubId))
      : [...current.warehouseIds, String(hubId)],
  }));

  const allWarehousesCount = banners.filter((banner) => banner.warehouse_id === null || banner.warehouse_id === undefined).length;
  const targetedCount = banners.length - allWarehousesCount;

  return (
    <div className="container">
      <section className="deals-banners-header">
        <div>
          <span className="deals-banners-eyebrow">Marketing · Mobile app</span>
          <h1>Deals banners</h1>
          <p>Control the promotional banners customers see in the app.</p>
        </div>
        <button className="btn btn-primary deals-banners-primary-action" type="button" onClick={() => setShowComposer((visible) => !visible)}>
          {showComposer ? 'Close composer' : '+ Add banner'}
        </button>
      </section>

      <section className="deals-banners-stats" aria-label="Banner summary">
        <div><span>Total banners</span><strong>{banners.length}</strong></div>
        <div><span>All warehouses</span><strong>{allWarehousesCount}</strong></div>
        <div><span>Hub-specific</span><strong>{targetedCount}</strong></div>
      </section>

      {showComposer && (
        <form className="deals-banners-composer" onSubmit={handleSubmit}>
          <div className="deals-banners-composer-heading">
            <div>
              <h2>Create a banner</h2>
              <p>Upload the creative, choose where it appears, then define the click behavior.</p>
            </div>
            <span className="deals-banners-live-badge">Will be active on publish</span>
          </div>
          <div className="deals-banners-composer-grid">
            <div className="deals-banners-image-field">
              <label htmlFor="deals-banner-image">Banner creative <em>*</em></label>
              <label className={`deals-banners-upload ${imagePreview ? 'has-preview' : ''}`} htmlFor="deals-banner-image">
                {imagePreview ? <img src={imagePreview} alt="Banner preview" /> : <><span className="deals-banners-upload-icon">↑</span><strong>Upload banner image</strong><small>JPEG, PNG or WebP · up to 10 MB</small></>}
              </label>
              <input key={fileInputKey} id="deals-banner-image" className="deals-banners-file-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => handleImageChange(event.target.files?.[0])} />
              {imagePreview && <button className="deals-banners-change-image" type="button" onClick={() => handleImageChange(null)}>Remove image</button>}
            </div>
            <div className="deals-banners-fields">
              <div className="deals-banners-field-row">
                <div className="form-group">
                  <label className="form-label">Click action</label>
                  <select className="form-control" value={form.actionType} onChange={(event) => setField('actionType', event.target.value)}>
                    <option value="route">Navigate to app route</option>
                    <option value="html">Show HTML content</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Display position</label>
                  <input className="form-control" type="number" min="0" value={form.position} onChange={(event) => setField('position', event.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Minimum app version</label>
                  <input className="form-control" type="number" min="0" placeholder="All versions" value={form.version} onChange={(event) => setField('version', event.target.value)} />
                </div>
              </div>
              <div className="form-group deals-banners-hub-field">
                <div className="deals-banners-label-row"><label className="form-label">Audience</label><label className="deals-banners-all-toggle"><input type="checkbox" checked={form.showAllWarehouses} onChange={(event) => setForm((current) => ({ ...current, showAllWarehouses: event.target.checked, warehouseIds: event.target.checked ? [] : current.warehouseIds }))} /> All warehouses</label></div>
                {!form.showAllWarehouses && (
                  <div className="deals-banners-picker-wrap">
                    <button className="form-control deals-banners-picker-trigger" type="button" onClick={() => setHubPickerOpen((open) => !open)}><span>{form.warehouseIds.length ? `${form.warehouseIds.length} hub${form.warehouseIds.length === 1 ? '' : 's'} selected` : 'Select hubs'}</span><span>⌄</span></button>
                    {hubPickerOpen && <div className="deals-banners-picker-menu"><div className="deals-banners-picker-actions"><button type="button" onClick={() => setField('warehouseIds', hubs.map((hub) => String(hub.id)))}>Select all</button><button type="button" onClick={() => setField('warehouseIds', [])}>Clear</button></div><div className="deals-banners-picker-list">{hubs.map((hub) => <label key={hub.id}><input type="checkbox" checked={form.warehouseIds.includes(String(hub.id))} onChange={() => toggleHub(hub.id)} />{hub.name}</label>)}</div></div>}
                  </div>
                )}
                <small>{form.showAllWarehouses ? 'This banner is shown to customers in every warehouse.' : 'Choose each warehouse where this banner should appear.'}</small>
              </div>
              {form.actionType === 'route' ? <div className="form-group"><label className="form-label">App route <em>*</em></label><input className="form-control" placeholder="/products or /deals" value={form.routePath} maxLength="500" onChange={(event) => setField('routePath', event.target.value)} /></div> : <div className="form-group"><label className="form-label">HTML content <em>*</em></label><textarea className="form-control deals-banners-html" rows="5" placeholder="&lt;h2&gt;Offer details&lt;/h2&gt;" value={form.htmlContent} onChange={(event) => setField('htmlContent', event.target.value)} /><small>This is sent to the app as banner detail content after a customer clicks it.</small></div>}
            </div>
          </div>
          <div className="deals-banners-composer-footer"><button className="btn btn-secondary" type="button" onClick={() => setShowComposer(false)}>Cancel</button><button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Publishing...' : 'Publish banner'}</button></div>
        </form>
      )}

      <section className="deals-banners-library">
        <div className="deals-banners-library-heading"><div><h2>Published banners</h2><p>{loading ? 'Loading your banners…' : `${banners.length} banner${banners.length === 1 ? '' : 's'} in the app`}</p></div></div>
        {!loading && banners.length === 0 ? <div className="deals-banners-empty"><span>▧</span><h3>No banners yet</h3><p>Create your first banner to promote a deal in the mobile app.</p><button className="btn btn-primary" type="button" onClick={() => setShowComposer(true)}>+ Add first banner</button></div> : <div className="deals-banners-grid">{banners.map((banner) => <article className="deals-banner-card" key={banner.id}><img src={banner.image_url} alt="Deals banner" /><div className="deals-banner-card-body"><div className="deals-banner-card-meta"><span>{hubName(banner.warehouse_id)}</span><span>Position {banner.position}</span></div><strong>{banner.show_banner_details ? 'Shows HTML content' : 'Navigates to route'}</strong><p>{banner.show_banner_details ? 'Banner details open after customer click.' : banner.route_path}</p><div className="deals-banner-card-footer"><span>{banner.version ? `App v${banner.version}+` : 'All app versions'}</span><button type="button" onClick={() => handleDelete(banner.id)} disabled={deletingId === banner.id}>{deletingId === banner.id ? 'Deleting…' : 'Delete'}</button></div></div></article>)}</div>}
      </section>
    </div>
  );
}
